import { Observer, observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNotification } from '@/Ship/Hooks/useNotification'
import { userManager } from '@/Core/Managers'
import { Form, FormField } from '@/Core/UI/Form'
import { createFormContext } from '@/Core/UI/Form/Providers'
import { FormRow } from '@/Ship/Components/Form/FormSection'
import { FormActionsBar } from '@/Ship/Components/UI/FormActionsBar'
import { cx } from '@/Ship/Helpers/cssClass'
import { parseApiError } from '@/Ship/Helpers/parseApiError'
import { formatPhone } from '@/Ship/Templates/Layouts/Components/UserProfileBlock/UserProfileBlock'
import { legacyFullName } from '@/Containers/SettingSection/UserContainer/Components/UserNameFields'
import { USER_ROLE_OPTIONS } from '@/Containers/SettingSection/UserContainer/Contracts/UserRole'

import { PersonalInfoStore } from '../../../Stores/PersonalInfoStore'

import { ProfilePhoto } from './ProfilePhoto'

import shell from '@/Ship/Components/UI/SettingsShell/SettingsShell.module.css'

import styles from '../UserProfile.module.css'

/** Поля, які користувач редагує сам; решта — лише для читання */
const EDITABLE_FIELDS = ['lastName', 'firstName', 'middleName', 'email'] as const

const normalize = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

/**
 * Розділ «Особиста інформація»: фото (ProfilePhoto, зберігається одразу), Прізвище, Ім'я, По-батькові і E-mail
 * з кнопкою «Зберегти», активною лише при змінах, і дані облікового запису лише для
 * читання (телефон-логін, посада, ролі), які змінює адміністратор у картці співробітника.
 */
export const PersonalInfo = observer(() => {
    const { t } = useTranslation()
    const { showSuccess, showError } = useNotification()
    const [store] = useState(() => new PersonalInfoStore())
    const [saving, setSaving] = useState(false)

    const tp = (key: string) => t(key, { ns: 'Setting.Profile' })

    /**
     * Опис помилки для повідомлення. HTTP 413 → «Файл занадто великий» лишається тут,
     * бо parseApiError навмисно не перекладає статуси (див. Ship/Helpers/parseApiError).
     */
    const describeError = async (err: any, fallbackKey: string): Promise<string> => {
        const parsed = await parseApiError(err)
        if (parsed.status === 413) return tp('File is too large. Maximum size is 3 MB.')
        if (parsed.violations?.length) return parsed.violations.map((v) => v.message).join('\n')
        if (parsed.message) return parsed.message
        return tp(fallbackKey)
    }

    const notify = (successMessage: string) => ({
        success: () => showSuccess(tp('Success'), successMessage),
        error: (msg: string) => showError(tp('Error'), msg),
    })

    const user = (userManager.user ?? {}) as Record<string, any>

    const [formContext] = useState(() =>
        createFormContext({
            fields: {
                // ПІБ бекенд збирає з трьох полів сам; окремо його не редагують
                lastName: {
                    type: 'text',
                    label: tp('Last name'),
                    isRequired: true,
                    inputProps: { placeholder: tp('Last name'), maxLength: 100 },
                },
                firstName: {
                    type: 'text',
                    label: tp('First name'),
                    isRequired: true,
                    inputProps: { placeholder: tp('First name'), maxLength: 100 },
                },
                middleName: {
                    type: 'text',
                    label: tp('Patronymic'),
                    inputProps: { placeholder: tp('Patronymic'), maxLength: 100 },
                },
                email: {
                    type: 'email',
                    label: 'E-mail',
                    isRequired: true,
                    inputProps: { placeholder: 'E-mail' },
                },
            },
            initialValues: {
                lastName: user['lastName'] || '',
                firstName: user['firstName'] || '',
                middleName: user['middleName'] || '',
                email: user['email'] || '',
            },
            onSubmit: async ({ values }: any) => {
                setSaving(true)
                try {
                    const payload = {
                        lastName: normalize(values.lastName),
                        firstName: normalize(values.firstName),
                        middleName: normalize(values.middleName),
                        email: normalize(values.email),
                    }
                    const ok = await store.submit(
                        { ...payload },
                        notify(tp('Information successfully updated.')),
                        (err) => describeError(err, 'Failed to save changes. Try again.')
                    )
                    if (ok) formContext.setInitialValues(payload)
                } finally {
                    setSaving(false)
                }
            },
        })
    )

    // Читається лише в <Observer> біля кнопок, не в рендері компонента: Form ядра на кожен свій
    // перерендер перемонтовує вміст (UIManager.render створює новий observer-компонент),
    // і перерендер на кожну літеру скидав би фокус поля
    const isDirty = () =>
        EDITABLE_FIELDS.some((key) => normalize(formContext.values[key]) !== normalize(formContext.initialValues[key]))

    // ── Обліковий запис (лише читання) ─────────────────────────────────────
    const roles: string[] = Array.isArray(user['roles']) ? user['roles'] : []
    const roleLabels = roles
        .filter((role) => role !== 'ROLE_USER')
        .map((role) => USER_ROLE_OPTIONS.find((option) => option.value === role)?.label ?? role)
    const position = typeof user['position'] === 'string' ? user['position'] : user['position']?.title
    const phone = user['phone'] ? formatPhone(String(user['phone'])) : ''

    // Давній ПІБ одним рядком, якого не видно з частин (див. UserNameFields)
    const legacy = legacyFullName(user)

    const empty = <span className={cx(styles, 'detailEmpty')}>{tp('Not set')}</span>

    return (
        <>
            <ProfilePhoto />

            <div className={cx(shell, 'divider')} />

            <Form formContext={formContext} onSubmit={() => void formContext.submit()} isLanguageSwitcher={false}>
                <div className={cx(shell, 'stack')}>
                    <div className={cx(shell, 'section')}>
                        <div className={cx(shell, 'sectionHead')}>
                            <h2 className={cx(shell, 'sectionTitle')}>{tp('Personal data')}</h2>
                            <p className={cx(shell, 'sectionHint')}>
                                {tp('Shown in the interface and used for notifications.')}
                            </p>
                        </div>
                        <FormRow>
                            <FormField name="lastName" />
                            <FormField name="firstName" />
                            <FormField name="middleName" />
                        </FormRow>
                        {legacy && (
                            <p className={cx(shell, 'sectionHint')} data-testid="legacy-full-name">
                                {tp('Full name was entered as a single line:')} «{legacy}».{' '}
                                {tp('Fill in last and first name.')}
                            </p>
                        )}
                        <div className={cx(shell, 'grid2')}>
                            <FormField name="email" />
                        </div>
                    </div>

                    <div className={cx(shell, 'divider')} />

                    <div className={cx(shell, 'section')}>
                        <div className={cx(shell, 'sectionHead')}>
                            <h2 className={cx(shell, 'sectionTitle')}>{tp('Account')}</h2>
                            <p className={cx(shell, 'sectionHint')}>
                                {tp('Changed by an administrator in the employee card.')}
                            </p>
                        </div>
                        <dl className={cx(styles, 'details')}>
                            <div className={cx(styles, 'detail')}>
                                <dt className={cx(styles, 'detailLabel')}>{tp('Phone (login)')}</dt>
                                <dd className={cx(styles, 'detailValue')}>{phone || empty}</dd>
                            </div>
                            <div className={cx(styles, 'detail')}>
                                <dt className={cx(styles, 'detailLabel')}>{tp('Position')}</dt>
                                <dd className={cx(styles, 'detailValue')}>{position || empty}</dd>
                            </div>
                            <div className={cx(styles, 'detail')}>
                                <dt className={cx(styles, 'detailLabel')}>{tp('Roles')}</dt>
                                <dd className={cx(styles, 'detailValue')}>
                                    {roleLabels.length > 0 ? (
                                        <span className={cx(styles, 'tags')}>
                                            {roleLabels.map((label) => (
                                                <span key={label} className={cx(styles, 'tag')}>
                                                    {label}
                                                </span>
                                            ))}
                                        </span>
                                    ) : (
                                        empty
                                    )}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div className={cx(shell, 'actions')}>
                        <Observer>
                            {() => (
                                <FormActionsBar
                                    dirty={isDirty()}
                                    saving={saving}
                                    onReset={() => formContext.reset()}
                                    submitLabel={tp('Save changes')}
                                    resetLabel={tp('Cancel')}
                                />
                            )}
                        </Observer>
                    </div>
                </div>
            </Form>
        </>
    )
})
